import {
  type ActiveCohortBuilderItemLegacy,
  type CohortBuilderDefinitionLegacy,
  CohortBuilderModalLegacy,
} from '@verantos-dev/types-cohorts';

export const getCohortBuilderItemForEditModalById = (
  id: string,
  model: CohortBuilderModalLegacy,
  cohortDefinition: CohortBuilderDefinitionLegacy | undefined
): ActiveCohortBuilderItemLegacy => {
  if (cohortDefinition === undefined) {
    throw new Error('cohortDefinition is undefined');
  }

  if (model === CohortBuilderModalLegacy.CONFIGURE_STUDY_PERIOD) {
    return {
      id,
      name: '',
      type: undefined,
      conceptFilters: [],
      numericFilters: [],
      eventFilters: [],
      temporalConstraints: [],
      inputFieldConfigMap: {},
      countFilter: undefined,
      indexDateValue: undefined,
    };
  }

  // indicates that this is a newly added cohort builder item if the model is add and there is no id
  if (
    [
      CohortBuilderModalLegacy.ADD_INDEX_DATE,
      CohortBuilderModalLegacy.ADD_SUBJECT_VARIABLE,
    ].includes(model)
  ) {
    return {
      id,
      name: '',
      type: undefined,
      conceptFilters: [],
      numericFilters: [],
      eventFilters: [],
      temporalConstraints: [],
      inputFieldConfigMap: {},
      countFilter: undefined,
      indexDateValue: undefined,
    };
  }

  // TODO: @chris.may - connect index date properly to the active cohort builder item state
  // Populate the cohort builder item with the currently defined index date
  if ([CohortBuilderModalLegacy.EDIT_INDEX_DATE].includes(model)) {
    const { indexDate } = cohortDefinition;
    return {
      id,
      type: indexDate?.type,
      inputFieldConfigMap: {},
      name: '', // this is not used for index date
      conceptFilters: [], // this is not used for index date
      numericFilters: [], // this is not used for index date
      eventFilters: [],
      temporalConstraints: [],
      countFilter: undefined,
      indexDateValue: indexDate?.indexDateValue,
      indexDateRangeValue: indexDate?.indexDateRangeValue,
    };
  }

  if ([CohortBuilderModalLegacy.EDIT_SUBJECT_VARIABLE].includes(model)) {
    const subjectVariable = cohortDefinition.subjectVariables?.find((sv) => sv.id === id);
    if (subjectVariable === undefined) {
      throw new Error(
        `getCohortBuilderItemForEditModalById: subjectVariable with id ${id} not found`
      );
    }
    return {
      id,
      name: subjectVariable.name ?? '',
      type: subjectVariable.type,
      conceptFilters: [...subjectVariable.conceptFilters],
      numericFilters: [...subjectVariable.numericFilters],
      countFilter: subjectVariable.countFilter,
      eventFilters: [],
      temporalConstraints: [],
      inputFieldConfigMap: {},
    };
  }

  if ([CohortBuilderModalLegacy.ADD_SIMPLE_EVENT].includes(model)) {
    return {
      id,
      name: '',
      type: undefined,
      conceptFilters: [],
      numericFilters: [],
      relatedEntityFilters: [],
      uniquenessFilters: [],
      eventFilters: [],
      temporalConstraints: [],
      inputFieldConfigMap: {},
      countFilter: undefined,
    };
  }

  if ([CohortBuilderModalLegacy.EDIT_SIMPLE_EVENT].includes(model)) {
    const simpleEvent = cohortDefinition.simpleEvents.find((se) => se.id === id);
    if (simpleEvent === undefined) {
      throw new Error(`getCohortBuilderItemForEditModalById: simpleEvent with id ${id} not found`);
    }
    return {
      id,
      name: simpleEvent.name ?? '',
      type: simpleEvent.type,
      conceptFilters: [...simpleEvent.conceptFilters],
      numericFilters: [...simpleEvent.numericFilters],
      relatedEntityFilters: [...simpleEvent.relatedEntityFilters],
      uniquenessFilters: [...simpleEvent.uniquenessFilters],
      eventFilters: [],
      temporalConstraints: [],
      inputFieldConfigMap: {},
      countFilter: undefined,
    };
  }

  if ([CohortBuilderModalLegacy.ADD_DERIVED_EVENT].includes(model)) {
    return {
      id,
      name: '',
      type: undefined,
      conceptFilters: [],
      numericFilters: [],
      eventFilters: [],
      temporalConstraints: [],
      inputFieldConfigMap: {},
      countFilter: undefined,
    };
  }

  if ([CohortBuilderModalLegacy.EDIT_DERIVED_EVENT].includes(model)) {
    const derivedEvent = cohortDefinition.derivedEvents.find((de) => de.id === id);
    if (derivedEvent === undefined) {
      throw new Error(`getCohortBuilderItemForEditModalById: derivedEvent with id ${id} not found`);
    }
    return {
      id,
      name: derivedEvent.name ?? '',
      type: derivedEvent.type,
      anyOrder: derivedEvent.anyOrder,
      temporalConstraints: derivedEvent.temporalConstraints ?? [],
      eventFilters: derivedEvent.eventFilters ?? [],
      uniquenessFilters: derivedEvent.uniquenessFilters ?? [],
      conceptFilters: [],
      numericFilters: [],
      inputFieldConfigMap: {},
      countFilter: undefined,
    };
  }

  throw new Error(`getCohortBuilderItemForEditModalById: model ${model} is not supported`);
};
